import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { useAuth } from "@/hooks/useAuth";
import { TransferRequestDetail } from "../schema/transfer-requests.schema";
import {
  cancelTransferRequest,
  CancelTransferRequestProps,
  getTransferRequest,
} from "@/services/transfer-requests.service";
import { TransferRequestMainDataCard } from "@/components/common/section-components/card/TransferRequestMainDataCard";
import { TransferRequestAmountAndActions } from "@/components/common/section-components/requests/transfer/TransferRequestAmountAndActions";
import { TransferRequestDetailLegend } from "@/components/common/section-components/requests/transfer/TransferRequestDetailLegend";
import { TransferRequestMessageList } from "@/components/common/section-components/requests/transfer/TransferRequestMessageList";
import { TransferTransactionType } from "./TransferTransactionType";
import { RequestSectionHeaderTitle } from "@/components/common/section-components/requests/RequestSectionHeaderTitle";
import { AxiosError } from "axios";
import { toaster } from "@/components/ui/toaster";
import { handlePermissionError } from "@/utils/permission-check";
import { useRoles } from "@/hooks/useRoles";

export const TransferRequestDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();

  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.TRANSFER_REQUESTS);
  };

  const [loadingRequest, setLoadingRequest] = useState<boolean>(false);

  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();

  const [request, setRequest] = useState<TransferRequestDetail | null>();

  const fetchRequest = async (showLoad: boolean = true) => {
    try {
    if (showLoad) setLoadingRequest(true);
    const response = await getTransferRequest({
      id: Number(id),
      usuarioActual: user?.name ?? "",
    });

    setRequest(response);

    setLoadingRequest(false);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.message ?? "No se pudo cargar la solicitud.",
        });
      }
      throw error;
    }
  };

  useEffect(() => {
    if (isActive && id && user) fetchRequest();
  }, [isActive, id, user]);

  const {
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(
      z.object({
        request_amount: z.any().optional(),
      }),
    ),
    defaultValues: {
      request_amount: request?.montoSolicitado,
    },
  });

  const watchedValues = useWatch({
    control,
  });

  useEffect(() => {
    if (request) {
      setValue("request_amount", request?.montoSolicitado);
    }
  }, [request]);

  useEffect(() => {
    clearErrors("request_amount");

    const monto = formatCurrencyInputToNumber(watchedValues?.request_amount);

    if (monto < 1) {
      setError("request_amount", {
        message: "El valor debe ser mayor o igual a $1.",
      });
    } else {
      clearErrors("request_amount");
    }
  }, [watchedValues?.request_amount, errors]);

  const [loadingRejectRequest, setLoadingRejectRequest] =
    useState<boolean>(false);

  const { fetchPermissions } = useRoles();

  const handleRejectRequest = async (event?: FieldValues) => {
    setLoadingRejectRequest(true);
    try {
      const objToSend: CancelTransferRequestProps = {
        id: Number(request?.id),
        mensaje: event?.request_detail,
        idMotivo: event?.message_motive?.[0],
        usuarioActual: user?.name,
      };

      await cancelTransferRequest(objToSend);

      toaster.create({
        type: "success",
        title: "Solicitud rechazada exitosamente.",
      });

      setLoadingRejectRequest(false);

      navigate(AppRoutes.TRANSFER_REQUESTS);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.response?.data?.message ??
            "No se pudo rechazar la solicitud.",
        });
        throw error;
      }
    } finally {
      setLoadingRejectRequest(false);
    }

    setOpenRejectModal(false);
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={
          <RequestSectionHeaderTitle request={request} type="Transferencia" />
        }
        onBack={handleBack}
        backButton
        loading={loadingRequest}
      />

      {!loadingRequest && (
        <TransferTransactionType
          request={request}
          loadingRejectRequest={loadingRejectRequest}
          loadingRequest={loadingRequest}
          setOpenRejectModal={setOpenRejectModal}
        />
      )}

      {!loadingRequest && (
        <>
          <TransferRequestAmountAndActions request={request} />

          <TransferRequestMainDataCard request={request} />

          <Flex
            flexDir="column"
            padding="12px"
            height="100%"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            borderRadius="12px"
            gap="25px"
          >
            <TransferRequestDetailLegend />

            <TransferRequestMessageList request={request} />
          </Flex>
        </>
      )}

      {request?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            isCancelling
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectRequest}
            loadingAccept={loadingRejectRequest}
            idTipoSolicitud={4}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);
